<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useProductPage } from '../../../business/use-product-page';
import { type Feedback, type Photo } from '@/shared/apollo-api/__generated__/types';
import { IconArrowLeft, IconArrowRight } from '@/features/ui-kit/icons';
import PhotoItem from './photo-item.vue';
import FullscreenSlider from '../fullscreen-slider.vue';
import CaptionGallery from '../caption-gallery.vue';

// Get product page state
const { state } = useProductPage();

// Interface for caption gallery slides
interface Slide {
  key: string;
  high: string;
  low: string;
  original: string;
  thumbnail: string;
  feedback: {
    id: string;
    rating: number;
    dateCreated: string;
    content: string;
    sku: {
      characteristicValues: Array<{
        characteristic: { title: string };
        title: string;
      }>;
    };
  };
}

// import { PageType } from '@mm-frontend/frontend-bunseki';
// import { clickstreamTracker } from '@/shared/clickstream';
// import { EUiPropertiesAction, EUiPropertiesSpaceType } from '@mm-frontend/clickstream-client-js';

interface Props {
  feedbackWithPhotos: Feedback[];
  feedbackWithPhotosLoading: boolean;
  itsFeedbackMobile?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itsFeedbackMobile: false,
});

const emit = defineEmits<{
  loadMoreFeedbackWithPhotos: [];
}>();

const canScrollRight = ref(true);
const canScrollLeft = ref(false);
const isShownFullscreen = ref(false);
const currentGalleryIndex = ref(0);

const photoSlider = ref<HTMLDivElement>();

const productId = computed(() => {
  return state.product?.id;
});

const photos = computed(() => {
  return isShownFullscreen.value
    ? props.feedbackWithPhotos
        .map(
          (feedback) =>
            feedback.photos?.map((photo: Photo) => ({
              key: photo.key,
              high: photo.link.high,
              low: photo.link.low,
              original: photo.original.high,
              thumbnail: photo.link.low,
              feedback: {
                id: String(feedback.id),
                rating: feedback.rating,
                dateCreated: feedback.dateCreated,
                content: feedback.content || '',
                sku: {
                  characteristicValues: feedback.sku.characteristicValues.map((char) => ({
                    characteristic: { title: char.characteristic.title },
                    title: char.title,
                  })),
                },
              },
            })) || []
        )
        .flat()
    : [];
});

const loadMoreFeedbackWithPhotos = () => {
  emit('loadMoreFeedbackWithPhotos');
};

const scrollRight = () => {
  if (!photoSlider.value) return;

  photoSlider.value.scrollBy({ behavior: 'smooth', left: photoSlider.value.clientWidth / 2 });
  loadMoreFeedbackWithPhotos();
};

const scrollLeft = () => {
  if (!photoSlider.value) return;

  photoSlider.value.scrollBy({ behavior: 'smooth', left: -photoSlider.value.clientWidth / 2 });
};

const openGallery = (photo: Photo, id: number) => {
  isShownFullscreen.value = true;
  eventShow(id);
  const index = photos.value.findIndex((p) => p.key === photo.key);
  currentGalleryIndex.value = index > 0 ? index : 0;
};

const closeGallery = () => {
  isShownFullscreen.value = false;
};

const eventShow = (id: string | number) => {
  console.log('Analytics: MEDIA_VIEW', {
    space_name: props.itsFeedbackMobile ? 'ProductReviews' : 'Product',
    space_type: 'PAGE',
    space_id: String(productId.value),
    action: 'SHOW',
    product_id: productId.value,
    feedback_id: id,
    is_full_screen: isShownFullscreen.value,
    media_type: 'photo',
    source_type: 'review',
    page_type: props.itsFeedbackMobile ? 'PRODUCT_REVIEWS' : 'PRODUCT',
  });
};

onMounted(() => {
  loadMoreFeedbackWithPhotos();

  const scrollHandler = () => {
    if (!photoSlider.value) return;

    const offset = 10;
    const photoSliderEl = photoSlider.value;
    const clientWidth = photoSliderEl.clientWidth;
    const scrollWidth = photoSliderEl.scrollWidth;

    canScrollRight.value = photoSliderEl.scrollLeft + clientWidth + offset < scrollWidth;
    canScrollLeft.value = photoSliderEl.scrollLeft > offset;

    if (photoSliderEl.scrollLeft + clientWidth > scrollWidth - clientWidth / 2) {
      loadMoreFeedbackWithPhotos();
    }
  };

  photoSlider.value?.addEventListener('scroll', scrollHandler);
  scrollHandler();
});

onUnmounted(() => {
  if (photoSlider.value) {
    photoSlider.value.removeEventListener('scroll', () => {});
  }
});
</script>

<template>
  <div
    :class="{
      'photo-slider-wrap': true,
      'photo-slider-wrap_can-scroll-right': canScrollRight,
      'photo-slider-wrap_can-scroll-left': canScrollLeft,
    }"
  >
    <div v-if="canScrollLeft" @click="scrollLeft()" class="btn-prev">
      <IconArrowLeft :size="16" />
    </div>
    <div v-if="canScrollRight" @click="scrollRight()" class="btn-next">
      <IconArrowRight :size="16" />
    </div>
    <!-- TODO: Consider replacing with keen-slider component when available for better performance and consistency -->
    <div ref="photoSlider" class="photo-slider">
      <template v-for="feedback in feedbackWithPhotos" :key="feedback.id">
        <template v-for="photo in feedback.photos" :key="photo.key">
          <PhotoItem
            @click="openGallery(photo, feedback.id)"
            @event-show-review="eventShow(feedback.id)"
            :photo="photo"
          />
        </template>
      </template>
    </div>

    <Teleport to="#desktop-fullscreen-slider">
      <transition name="fade" mode="out-in">
        <caption-gallery
          v-if="isShownFullscreen"
          :slides="photos"
          :index="currentGalleryIndex"
          :alt="'123'"
          @close="closeGallery"
          @change="eventShow"
        />
      </transition>
    </Teleport>
  </div>
</template>

<style scoped lang="stylus">
$gap = 12px
$height = 145px

scroll-hidden()
  content ''
  position absolute
  top 0
  height $height
  width 51px
  opacity 0
  transition opacity .3s ease

.photo-slider-wrap
  position relative

  &::before
    pointer-events none
    left -1px
    scroll-hidden()
    background: linear-gradient(90deg, #FFF 0%, rgba(255, 255, 255, 0.69) 34.42%, rgba(255, 255, 255, 0.41) 68.05%, rgba(255, 255, 255, 0.00) 100%);

  &::after
    pointer-events none
    right -1px
    scroll-hidden()
    background: linear-gradient(270deg, #FFF 0%, rgba(255, 255, 255, 0.69) 34.42%, rgba(255, 255, 255, 0.41) 68.05%, rgba(255, 255, 255, 0.00) 100%);

  &_can-scroll-right::after
    opacity 1

  &_can-scroll-left::before
    opacity 1

.photo-slider
  display flex
  flex-direction row
  overflow-y hidden
  overflow-x scroll
  align-items flex-start
  gap $gap
  height $height

  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }

  .photo-item
    cursor pointer
    img
      height 145px
      width auto
      aspect-ratio 3/4
      object-fit cover
      border-radius: 12px;

btn()
  position absolute
  top 0
  z-index 2
  width: 40px;
  height: 40px;
  cursor pointer
  background: rgba(255, 255, 255, 0.60);
  filter: drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.04));
  backdrop-filter: blur(2px);
  border-radius 100%
  display flex
  justify-content center
  align-items center

.btn-next
  btn()
  right 6px
  transform "translateY(calc(%s / 2 - 50%)) translateX(50%)" % $height


.btn-prev
  btn()
  left 6px
  transform "translateY(calc(%s / 2 - 50%)) translateX(-50%)" % $height
</style>
